import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    alpha,
    Box,
    Collapse,
    Paper,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'
import useStyles from './styles'
import TableHeaderCell from './TableHeaderCell'
import TableRowCell from './TableRowCell'
export type TableRowComponentProps<T> = {
    row: T
    index: number
    className: string
    onClick: () => void
    children: React.ReactNode
    rowRef?: React.Ref<HTMLTableRowElement>
}

export interface CustomColumnDef<T> {
    field: string
    header: string | React.ReactNode
    headerSxProps?: SxProps
    tableSxProps?: SxProps
    sx?: SxProps
    align?: 'left' | 'right' | 'center'
    sortable?: boolean
    colSpan?: number
    cellRenderer?: (row: T, index: number) => React.ReactNode | Promise<React.ReactNode>
    tableCellClassName?: string
    /** Stable id for React keys / frozen live cells when visible column order changes (e.g. ltp vs ltpChange). */
    cellIdentityKey?: string
}

interface CustomTableProps<T> extends React.HTMLAttributes<HTMLDivElement> {
    tableContainerSxProps?: SxProps
    tableSxProps?: SxProps
    className?: string
    columns: CustomColumnDef<T>[]
    rows: T[]
    onSort?: (field: string) => void
    keyFieldInData: string
    hideHeader?: boolean
    verticalDividers?: boolean
    sortField?: string
    sortDirection?: 'asc' | 'desc'
    RenderCollapsibleContent?: React.FC<{
        row: T
        index: number
    }>
    disableColGroup?: boolean
    rowHoverElement?: (index: number) => JSX.Element
    collapseComponentProps?: any
    expandedRows?: string[]
    toggleExpandedRow?: (index: number) => void
    getRowClassName?: (row: T, index: number) => string
    loading?: boolean
    renderEmptyData?: (loading: boolean, colSpan: number) => JSX.Element
    onRowClick?: (row: T, index: number) => void;
    tableRef?: React.RefObject<HTMLDivElement>,
    totalCount?: number
    singleViewTable?: boolean
    strikeColWidth?: number
    skeletalLoading?: boolean
    TableRowComponent?: React.ComponentType<TableRowComponentProps<T>>
    /**
     * @deprecated Prefer stableLiveContent — live cells subscribe internally.
     * When set, still busts memoized table body (avoid on option chain).
     */
    liveMarketDataToken?: unknown
    /** Mount each cell once; live text updates without remounting TableCell. */
    stableLiveContent?: boolean
}

const DefaultTableRow = <T,>({
    className,
    onClick,
    children,
    rowRef,
}: TableRowComponentProps<T>) => (
    <TableRow
        ref={rowRef}
        sx={{
            position: 'relative',
            '&:hover .MuiTableCell-root': {
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#141414' : '#f8f8f8'),
            },
            '&.selected-row .MuiTableCell-root': {
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#181818' : '#f4f4f4'),
            },
            '&:hover .action-btns': {
                visibility: 'visible !important',
            },
        }}
        className={className}
        onClick={onClick}
    >
        {children}
    </TableRow>
)

function CustomTable<T>({
    className,
    columns,
    rows,
    onSort,
    hideHeader = false,
    disableColGroup = false,
    verticalDividers = true,
    sortField,
    sortDirection = 'asc',
    RenderCollapsibleContent,
    rowHoverElement,
    collapseComponentProps,
    expandedRows = [],
    toggleExpandedRow,
    keyFieldInData,
    getRowClassName,
    loading = false,
    renderEmptyData,
    onRowClick,
    tableRef,
    totalCount,
    tableContainerSxProps = {},
    tableSxProps = {},
    singleViewTable = false,
    strikeColWidth,
    skeletalLoading = false,
    TableRowComponent: TableRowComponentProp,
    liveMarketDataToken,
    stableLiveContent = false,
}: CustomTableProps<T>) {
    const TableRowComponent = TableRowComponentProp ?? DefaultTableRow
    const { classes } = useStyles()
    const [firstPaintDone, setFirstPaintDone] = useState(false)
    const firstRowRef = useRef<HTMLTableRowElement>(null);

    useEffect(() => {
        if (!firstRowRef.current) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setFirstPaintDone(true);
                observer.disconnect();
            }
        });
        observer.observe(firstRowRef.current);
        return () => observer.disconnect();
    }, [rows]);


    const handleSort = useCallback(
        (field: string) => {
            if (onSort) onSort(field)
        },
        [onSort]
    )

    const handleRowClick = useCallback(
        (row: T, index: number) => {
            if (onRowClick) {
                onRowClick(row, index)
            } else if (RenderCollapsibleContent) {
                toggleExpandedRow && toggleExpandedRow(index)
            }
        },
        [onRowClick, RenderCollapsibleContent, toggleExpandedRow]
    )

    const tableHeader = useMemo(
        () => (
            <TableHead>
                <TableRow>
                    {columns.map((column, index) =>
                        column.colSpan === 0 ? null : (
                            <TableHeaderCell<T>
                                key={column.field + index}
                                column={column}
                                verticalDividers={verticalDividers}
                                index={index}
                                columnLength={columns.length}
                                sxProps={column.headerSxProps}
                                sortField={sortField}
                                sortDirection={sortDirection}
                                handleSort={handleSort}
                            />
                        )
                    )}
                </TableRow>
            </TableHead>
        ),
        [columns, handleSort, sortDirection, sortField, verticalDividers]
    )

    const tableBody = useMemo(
        () => {
            const hasData = (rows?.length ?? 0) > 0
            return (
                <>
                    {hasData
                        ? rows.map((row: any, index) => (
                            <React.Fragment key={row?.[keyFieldInData] ?? index}>
                                {
                                    <TableRowComponent
                                        row={row}
                                        index={index}
                                        rowRef={index === 0 ? firstRowRef : undefined}
                                        className={getRowClassName ? getRowClassName(row, index) : ''}
                                        onClick={() => handleRowClick(row, index)}
                                    >
                                        {columns.map((column, columnIndex) => (
                                            <TableRowCell<T>
                                                key={`${row?.[keyFieldInData] ?? index}-cell-${column.cellIdentityKey ?? column.field}-${columnIndex}`}
                                                column={column}
                                                row={row}
                                                rowKey={row?.[keyFieldInData] ?? index}
                                                columnLength={columns.length}
                                                columnIndex={columnIndex}
                                                index={index}
                                                verticalDividers={verticalDividers}
                                                skeletalLoading={skeletalLoading}
                                                stableLiveContent={stableLiveContent}
                                            />
                                        ))}

                                        {!singleViewTable && rowHoverElement ? (
                                            <TableCell
                                                sx={{
                                                    padding: 0,
                                                    width: 0,
                                                    minWidth: 0,
                                                    maxWidth: 0,
                                                    overflow: 'visible',
                                                    position: 'relative',
                                                    verticalAlign: 'middle',
                                                    borderRight: 'none !important',
                                                    borderBottomColor: (theme) => theme.palette.divider,
                                                }}
                                            >
                                                {rowHoverElement(index)}
                                            </TableCell>
                                        ) : null}
                                    </TableRowComponent>
                                }
                                {RenderCollapsibleContent && (
                                    <TableRow sx={{ position: 'relative' }}>
                                        <TableCell className={classes.tableCell} sx={!expandedRows.includes(index.toString()) ? { borderBottom: 'none !important' } : {}} colSpan={columns.length}>
                                            <Collapse in={expandedRows.includes(index.toString())} timeout="auto" unmountOnExit>
                                                <Box sx={{ width: '100%', overflow: 'visible' }}>
                                                    {RenderCollapsibleContent && <RenderCollapsibleContent row={row} index={index} {...collapseComponentProps} tableSxProps={tableSxProps} />}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        ))
                        : renderEmptyData
                            ? renderEmptyData(loading, columns.length)
                            : null}
                    {firstPaintDone && hasData && totalCount !== undefined && rows?.length !== totalCount && (
                        <TableRow sx={{ position: 'relative' }}>
                            <TableCell colSpan={columns.length} align="center" sx={{ fontWeight: 'bold', padding: '10px' }}>
                                <Box sx={{ position: 'sticky', width: 'max-content', left: '50%', transform: 'translateX(-50%)' }}>
                                    <Typography variant="body2" component="div">
                                        Showing {rows.length} of {totalCount} results
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" component="div">
                                        Refine your filters to get more relevant results.
                                    </Typography>
                                </Box>
                            </TableCell>
                        </TableRow>
                    )}
                </>
            )
        },
        [RenderCollapsibleContent, TableRowComponent, classes.tableCell, collapseComponentProps, columns, expandedRows, getRowClassName, keyFieldInData, loading, liveMarketDataToken, renderEmptyData, rows, totalCount, verticalDividers, handleRowClick, rowHoverElement, singleViewTable, strikeColWidth, skeletalLoading, stableLiveContent]
    )


    const colGroup = useMemo(() => {
        if (disableColGroup) return null
        return (
            <colgroup>
                {columns.map((column, index) => {
                    return <col key={`${index}-col`} style={{ width: (column?.sx as any).width ?? 'auto', minWidth: (column?.sx as any).minWidth ?? 'auto' }} />
                })}
            </colgroup>
        )
    }, [columns])

    const headerView = useMemo(() => {
        return hideHeader ? null : tableHeader
    }, [hideHeader, tableHeader])

    const jsxTree = useMemo(() => {
        return (
            <TableContainer sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...tableContainerSxProps }} component={Paper}
                className={classes.container} ref={tableRef}>
                <Table size={"small"} className={`${classes.table} ${className ? className : ''}`} stickyHeader sx={{
                    '& tr.synced-hover .MuiTableCell-root': {
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                                ? `${alpha(theme.palette.common.white, 0.03)} !important`
                                : alpha(theme.palette.common.black, 0.02),
                    }, ...tableSxProps
                }}>
                    {!disableColGroup && colGroup}
                    {headerView}
                    <TableBody className={className ? `${className}-body` : ''} sx={{ ...tableSxProps }}>{tableBody}</TableBody>
                </Table>
            </TableContainer>
        )
    }, [classes.container, classes.table, className, colGroup, tableBody, tableRef, headerView, tableContainerSxProps, tableSxProps])

    return jsxTree
}

export default CustomTable
